import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../../../components-v2/Button';
import {
	useOpportunityReportingOwnerOpportunitiesQuery,
	useOpportunityReportingStageOwnersQuery,
	useOpportunityReportingStagesQuery,
} from '../../../../../../../queries';
import { formatCurrency } from '../../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface StagesTableProps {
	filterRequest: Api.IOpportunityReportingFilterRequest;
	boardId: string;
	enabled: boolean;
}

interface OpportunityRowProps {
	opportunity: Api.IOpportunityReportingOpportunityDetail;
	boardId: string;
	depth: number;
}

const OpportunityRow: React.FC<OpportunityRowProps> = ({ opportunity, boardId, depth }) => (
	<tr className={css(styleSheet.tr)}>
		<td className={css(styleSheet.td)} style={{ paddingLeft: depth * 20 + 12 }}>
			<a
				href={`#/dataBoards/opportunities/${boardId}/${opportunity.id}/`}
				className={css(styleSheet.opportunityLink)}
				target='_blank'
				rel='noopener'
			>
				{opportunity.name}
			</a>
		</td>
		<td className={css(styleSheet.td)}>1</td>
		{/* always 1 — each row is a single opportunity */}
		<td className={css(styleSheet.td)}>{formatCurrency(opportunity.dealSize)}</td>
		<td className={css(styleSheet.td)}>
			{opportunity.creationDate ? moment(opportunity.creationDate).format('MM/DD/YYYY') : '—'}
		</td>
		<td className={css(styleSheet.td)}>
			{opportunity.closeDate ? moment(opportunity.closeDate).format('MM/DD/YYYY') : '—'}
		</td>
	</tr>
);

interface OwnerRowProps {
	owner: Api.IOpportunityReportingOwnerSummary;
	filterRequest: Api.IOpportunityReportingFilterRequest;
	boardId: string;
	stageId: string;
	depth: number;
}

const OwnerRow: React.FC<OwnerRowProps> = ({ owner, filterRequest, boardId, stageId, depth }) => {
	const [isExpanded, setIsExpanded] = React.useState(false);

	const opportunitiesQuery = useOpportunityReportingOwnerOpportunitiesQuery({
		boardId,
		filterRequest,
		stageId,
		ownerId: owner.id,
		enabled: isExpanded,
	});

	const allOpportunities = opportunitiesQuery.data?.pages.flatMap(p => p.values) ?? [];

	return (
		<>
			<tr className={css(styleSheet.tr)}>
				<td className={css(styleSheet.td)}>
					<div className={css(styleSheet.expandCell)} style={{ paddingLeft: depth * 20 + 4 }}>
						<Button
							type='button'
							variant='ghost'
							styles={[styleSheet.expandToggle]}
							onClick={() => setIsExpanded(e => !e)}
							aria-label={isExpanded ? 'Collapse' : 'Expand'}
						>
							{isExpanded ? '▼' : '▶'}
						</Button>
						<span>{owner.name}</span>
					</div>
				</td>
				<td className={css(styleSheet.td)}>{owner.count}</td>
				<td className={css(styleSheet.td)}>{formatCurrency(owner.totalDealSize)}</td>
				<td className={css(styleSheet.td)}>—</td>
				<td className={css(styleSheet.td)}>—</td>
			</tr>
			{isExpanded && opportunitiesQuery.isPending && (
				<tr className={css(styleSheet.tr)}>
					<td className={css(styleSheet.td)} colSpan={5} style={{ paddingLeft: (depth + 1) * 20 }}>
						Loading opportunities…
					</td>
				</tr>
			)}
			{isExpanded &&
				allOpportunities.map(opp => (
					<OpportunityRow key={opp.id} opportunity={opp} boardId={boardId} depth={depth + 1} />
				))}
			{isExpanded && opportunitiesQuery.hasNextPage && (
				<tr className={css(styleSheet.tr)}>
					<td className={css(styleSheet.td)} colSpan={5}>
						<Button
							variant='secondary'
							onClick={() => {
								void opportunitiesQuery.fetchNextPage();
							}}
						>
							Load More
						</Button>
					</td>
				</tr>
			)}
		</>
	);
};

interface StageRowProps {
	stage: Api.IOpportunityReportingStageSummary;
	filterRequest: Api.IOpportunityReportingFilterRequest;
	boardId: string;
	depth: number;
}

const StageRow: React.FC<StageRowProps> = ({ stage, filterRequest, boardId, depth }) => {
	const [isExpanded, setIsExpanded] = React.useState(false);

	const ownersQuery = useOpportunityReportingStageOwnersQuery({
		boardId,
		filterRequest,
		stageId: stage.id,
		enabled: isExpanded,
	});

	return (
		<>
			<tr className={css(styleSheet.tr)}>
				<td className={css(styleSheet.td)}>
					<div className={css(styleSheet.expandCell)} style={{ paddingLeft: depth * 20 + 4 }}>
						<Button
							type='button'
							variant='ghost'
							styles={[styleSheet.expandToggle]}
							onClick={() => setIsExpanded(e => !e)}
							aria-label={isExpanded ? 'Collapse' : 'Expand'}
						>
							{isExpanded ? '▼' : '▶'}
						</Button>
						<span>{stage.name}</span>
					</div>
				</td>
				<td className={css(styleSheet.td)}>{stage.count}</td>
				<td className={css(styleSheet.td)}>{formatCurrency(stage.totalDealSize)}</td>
				<td className={css(styleSheet.td)}>—</td>
				<td className={css(styleSheet.td)}>—</td>
			</tr>
			{isExpanded && ownersQuery.isPending && (
				<tr className={css(styleSheet.tr)}>
					<td className={css(styleSheet.td)} colSpan={5} style={{ paddingLeft: (depth + 1) * 20 }}>
						Loading owners…
					</td>
				</tr>
			)}
			{isExpanded &&
				(ownersQuery.data?.owners ?? []).map(owner => (
					<OwnerRow
						key={owner.id}
						owner={owner}
						filterRequest={filterRequest}
						boardId={boardId}
						stageId={stage.id}
						depth={depth + 1}
					/>
				))}
		</>
	);
};

export const StagesTable: React.FC<StagesTableProps> = ({ filterRequest, boardId, enabled }) => {
	const stagesQuery = useOpportunityReportingStagesQuery({
		boardId,
		filterRequest,
		enabled,
	});

	const stages = stagesQuery.data?.stages ?? [];

	return (
		<div className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.title)}>Opportunities by Stage</h3>
			{!enabled ? (
				<p className={css(styleSheet.emptyState)}>Select a board to view stage details.</p>
			) : stagesQuery.isPending ? (
				<p className={css(styleSheet.emptyState)}>Loading…</p>
			) : stages.length === 0 ? (
				<div className={css(styleSheet.emptyStateFiltered)}>
					<p className={css(styleSheet.emptyStateFilteredTitle)}>No results found</p>
					<p className={css(styleSheet.emptyStateFilteredMessage)}>
						No opportunities match your current filters. Try adjusting or clearing them.
					</p>
				</div>
			) : (
				<div className={css(styleSheet.tableWrapper)}>
					<table className={css(styleSheet.table)}>
						<thead>
							<tr>
								<th className={css(styleSheet.th)}>Stage</th>
								<th className={css(styleSheet.th)}>Count</th>
								<th className={css(styleSheet.th)}>Deal Size</th>
								<th className={css(styleSheet.th)}>Creation Date</th>
								<th className={css(styleSheet.th)}>Close Date</th>
							</tr>
						</thead>
						<tbody>
							{stages.map(stage => (
								<StageRow key={stage.id} stage={stage} filterRequest={filterRequest} boardId={boardId} depth={0} />
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
